import { useMemo } from 'react';
import { PlusIcon } from '@interstate/components/Icons';
import { Action } from '@interstate/components/Action';
import { useCustomerCardContext } from '../CustomerCardProvider';
import { CustomerAddressForm } from '../customerAddresses/CustomerAddressForm';
import { VIEW_MODE } from '../CustomerCardEnums';
import { CustomerFieldsAccordionContainer, ExpandedSectionCard } from './HousingAndEmploymentInfo.style';
import {
    MONTHS_AT_PREVIOUS_ADDRESS_FIELD,
    PREVIOUS_ADDRESS_CITY_FIELD,
    PREVIOUS_ADDRESS_COUNTY_FIELD,
    PREVIOUS_ADDRESS_STATE_FIELD,
    PREVIOUS_ADDRESS_STREET_2_FIELD,
    PREVIOUS_ADDRESS_STREET_FIELD,
    PREVIOUS_ADDRESS_ZIP_FIELD,
    YEARS_AT_PREVIOUS_ADDRESS_FIELD
} from '../constants';
import { CustomerFieldsLayoutHeader } from './CustomerFieldsLayoutHeader';
import type { ErrorState } from '@makemydeal/dr-dash-ui-utils';

const EMPTY_ERROR_STATE: ErrorState = { hasError: false, errorMessage: '' };

const FIELD_MAP: Record<string, string> = {
    streetAddress: PREVIOUS_ADDRESS_STREET_FIELD,
    streetAddress2: PREVIOUS_ADDRESS_STREET_2_FIELD,
    city: PREVIOUS_ADDRESS_CITY_FIELD,
    state: PREVIOUS_ADDRESS_STATE_FIELD,
    zip: PREVIOUS_ADDRESS_ZIP_FIELD,
    county: PREVIOUS_ADDRESS_COUNTY_FIELD
};

export const PreviousAddress = () => {
    const {
        isPreviousAddressExpanded,
        setIsPreviousAddressExpanded,
        isLocked,
        formValues,
        handleInputChange,
        handleNumberInputChange,
        customerFormErrors
    } = useCustomerCardContext();

    const previousAddressFormErrors = useMemo(() => {
        const previousAddressErrors = customerFormErrors?.previousAddress as Record<string, ErrorState> | undefined;
        return {
            streetAddress: previousAddressErrors?.streetAddress ?? EMPTY_ERROR_STATE,
            streetAddress2: previousAddressErrors?.streetAddress2 ?? EMPTY_ERROR_STATE,
            city: previousAddressErrors?.city ?? EMPTY_ERROR_STATE,
            zip: previousAddressErrors?.zip ?? EMPTY_ERROR_STATE,
            county: previousAddressErrors?.county ?? EMPTY_ERROR_STATE,
            yearsAtAddress: (customerFormErrors?.yearsAtPreviousAddress as ErrorState) ?? EMPTY_ERROR_STATE,
            monthsAtAddress: (customerFormErrors?.monthsAtPreviousAddress as ErrorState) ?? EMPTY_ERROR_STATE
        };
    }, [customerFormErrors]);

    const previousAddressValues = useMemo(
        () => ({
            streetAddress: formValues.previousAddress?.streetAddress ?? '',
            streetAddress2: formValues.previousAddress?.streetAddress2 ?? '',
            city: formValues.previousAddress?.city ?? '',
            state: formValues.previousAddress?.state ?? '',
            zip: formValues.previousAddress?.zip ?? '',
            county: formValues.previousAddress?.county ?? '',
            yearsAtAddress: String(formValues.yearsAtPreviousAddress ?? ''),
            monthsAtAddress: String(formValues.monthsAtPreviousAddress ?? '')
        }),
        [formValues]
    );

    const handleAddressFormChange = (field: string, value: string) => {
        if (field === 'yearsAtAddress') {
            handleNumberInputChange(YEARS_AT_PREVIOUS_ADDRESS_FIELD, value);
            return;
        }
        if (field === 'monthsAtAddress') {
            handleNumberInputChange(MONTHS_AT_PREVIOUS_ADDRESS_FIELD, value);
            return;
        }
        handleInputChange(FIELD_MAP[field], value);
    };

    const handleToggle = () => {
        setIsPreviousAddressExpanded((prev) => !prev);
    };

    const handleDelete = () => {
        [
            PREVIOUS_ADDRESS_STREET_FIELD,
            PREVIOUS_ADDRESS_STREET_2_FIELD,
            PREVIOUS_ADDRESS_CITY_FIELD,
            PREVIOUS_ADDRESS_STATE_FIELD,
            PREVIOUS_ADDRESS_ZIP_FIELD,
            PREVIOUS_ADDRESS_COUNTY_FIELD
        ].forEach((field) => {
            handleInputChange(field, '');
        });

        [YEARS_AT_PREVIOUS_ADDRESS_FIELD, MONTHS_AT_PREVIOUS_ADDRESS_FIELD].forEach((field) => {
            handleNumberInputChange(field, '');
        });

        setIsPreviousAddressExpanded(false);
    };

    return (
        <CustomerFieldsAccordionContainer data-testid="previous-address-section">
            {!isPreviousAddressExpanded ? (
                <Action
                    data-testid="previous-address-button"
                    actionIcon={{ icon: <PlusIcon />, start: true }}
                    onClick={handleToggle}
                >
                    Add Previous Address
                </Action>
            ) : (
                <ExpandedSectionCard>
                    <CustomerFieldsLayoutHeader
                        onDelete={handleDelete}
                        deleteTestId="previous-address-delete-button"
                        disabled={isLocked}
                    >
                        Previous Address
                    </CustomerFieldsLayoutHeader>
                    <CustomerAddressForm
                        formErrors={previousAddressFormErrors}
                        values={previousAddressValues}
                        handleInputChange={handleAddressFormChange}
                        viewMode={VIEW_MODE.FULL}
                        disabled={isLocked}
                        showTimeAtAddressFields
                        isPostalCodeRequired={false}
                    />
                </ExpandedSectionCard>
            )}
        </CustomerFieldsAccordionContainer>
    );
};
